<!-- src/components/modal/invoices-modal.vue -->
<script setup>
import { computed } from "vue";
import { numberToWords } from "@/utils/numberToWords";

const props = defineProps({
  invoice: { type: Object, default: () => ({}) },
  items: { type: Array, default: () => [] },
  company: { type: Object, default: () => null },
  customer: { type: Object, default: () => null },
  totals: {
    type: Object,
    default: () => ({
      subTotal: 0,
      discountTotal: 0,
      taxTotal: 0,
      grandTotal: 0,
    }),
  },
});

const companyLogo = computed(() => {
  return (
    props.company?.logo_url ||
    props.company?.logo ||
    props.company?.company_logo ||
    null
  );
});

const signatureImage = computed(() => {
  return (
    props.company?.signatory_signature_url ||
    props.company?.signature_url ||
    props.company?.signatory_signature ||
    props.company?.signature ||
    null
  );
});

const qrImageSource = computed(() => {
  const value =
    props.invoice?.etims_qr_code ||
    props.invoice?.qr_code ||
    props.invoice?.qr_image_url ||
    null;

  if (!value || typeof value !== "string") return null;

  if (
    value.startsWith("data:image/") ||
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("/")
  ) {
    return value;
  }

  return null;
});

const qrFallbackText = computed(() => {
  return (
    props.invoice?.etims_control_code ||
    props.invoice?.etims_reference_no ||
    props.invoice?.invoice_no ||
    "Pending QR"
  );
});

const companyInitials = computed(() => {
  const name = props.company?.company_name || "";
  if (!name) return "CN";
  return name
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
});

function formatMoney(value) {
  const num = Number(value || 0);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

const amountInWords = computed(() => {
  const amt = Number(props.totals.grandTotal || 0);
  return `Amount in Words : ${numberToWords(amt)} only`;
});

const statusLabel = computed(() => {
  const st = (props.invoice.status || "").toLowerCase();
  switch (st) {
    case "draft":
      return "Draft";
    case "pending":
      return "Pending";
    case "paid":
      return "Paid";
    case "overdue":
      return "Overdue";
    default:
      return "Pending";
  }
});

const paymentDisplayName = computed(() => {
  return (
    props.company?.account_holder_name ||
    props.company?.company_name ||
    "Company Name"
  );
});

const paymentInstruction = computed(() => {
  return (
    props.company?.payment_instructions ||
    `Payment may be made via bank transfer or cheque in the name of ${paymentDisplayName.value}.`
  );
});

const statusClass = computed(() => {
  const st = (props.invoice.status || "").toLowerCase();
  switch (st) {
    case "draft":
      return "badge-soft-secondary";
    case "pending":
      return "badge-soft-warning";
    case "paid":
      return "badge-soft-success";
    case "overdue":
      return "badge-soft-danger";
    default:
      return "badge-soft-secondary";
  }
});
</script>

<template>
  <!-- Invoice Preview -->
  <div class="modal fade" id="invoice_preview">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-body p-4">
          <div class="invoice-content">
            <div class="d-flex justify-content-center align-items-center">
              <div class="flex-fill">
                <!-- Header -->
                <div
                  class="row justify-content-between align-items-center border-bottom mb-3"
                >
                  <div class="col-md-6">
                    <div class="mb-2">
                      <template v-if="companyLogo">
                        <img :src="companyLogo" class="img-fluid" alt="logo" />
                      </template>
                      <template v-else>
                        <div
                          class="avatar avatar-lg bg-primary text-white d-inline-flex align-items-center justify-content-center rounded"
                        >
                          <span class="fw-bold">{{ companyInitials }}</span>
                        </div>
                      </template>
                    </div>

                    <p>
                      {{ company?.address || "Company address" }}
                    </p>
                  </div>
                  <div class="col-md-6">
                    <div class="text-end mb-3">
                      <h5 class="text-gray mb-1">
                        Invoice No
                        <span class="text-primary">
                          #{{ invoice.invoice_no || "INV-XXX" }}
                        </span>
                      </h5>
                      <p class="mb-1 fw-medium">
                        Created Date :
                        <span class="text-dark">
                          {{ formatDate(invoice.invoice_date) }}
                        </span>
                      </p>
                      <p class="fw-medium">
                        Due Date :
                        <span class="text-dark">
                          {{ formatDate(invoice.due_date) }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- From / To -->
                <div class="row border-bottom mb-3">
                  <div class="col-md-5">
                    <p class="text-dark mb-2 fw-semibold">From</p>
                    <div>
                      <h4 class="mb-1">
                        {{ company?.company_name || "Company Name" }}
                      </h4>
                      <p class="mb-1">
                        {{ company?.address || "Company address" }}
                      </p>
                      <p class="mb-1">
                        Email :
                        <span class="text-dark">
                          {{ company?.email || "company@example.com" }}
                        </span>
                      </p>
                      <p>
                        Phone :
                        <span class="text-dark">
                          {{ company?.phone || "+000 000 000" }}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div class="col-md-5">
                    <p class="text-dark mb-2 fw-semibold">To</p>
                    <div>
                      <h4 class="mb-1">
                        {{ customer?.name || "Customer Name" }}
                      </h4>
                      <p class="mb-1">
                        {{ customer?.address || "Customer address" }}
                      </p>
                      <p class="mb-1">
                        Email :
                        <span class="text-dark">
                          {{ customer?.email || "customer@example.com" }}
                        </span>
                      </p>
                      <p>
                        Phone :
                        <span class="text-dark">
                          {{ customer?.phone || "" }}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="mb-3">
                      <p class="text-title mb-2 fw-medium">Payment Status</p>
                      <span
                        class="badge align-items-center mb-3"
                        :class="statusClass"
                      >
                        <i class="ti ti-point-filled"></i>
                        {{ statusLabel }}
                      </span>
                      <div>
                        <img
                          v-if="qrImageSource"
                          :src="qrImageSource"
                          class="img-fluid"
                          alt="QR"
                        />
                        <div v-else class="invoice-qr-fallback">
                          <span class="invoice-qr-caption">Reference</span>
                          <strong>{{ qrFallbackText }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Items -->
                <div>
                  <p class="fw-medium">
                    Invoice For :
                    <span class="text-dark fw-medium">
                      {{ invoice.title || "—" }}
                    </span>
                  </p>
                  <div class="table-responsive mb-3">
                    <table class="table">
                      <thead class="thead-light">
                        <tr>
                          <th>Description</th>
                          <th class="text-end">Qty</th>
                          <th class="text-end">Cost</th>
                          <th class="text-end">Discount</th>
                          <th class="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, idx) in items" :key="idx">
                          <td>
                            <h6>{{ item.description }}</h6>
                          </td>
                          <td class="text-gray-9 fw-medium text-end">
                            {{ item.qty }}
                          </td>
                          <td class="text-gray-9 fw-medium text-end">
                            {{ formatMoney(item.rate) }}
                          </td>
                          <td class="text-gray-9 fw-medium text-end">
                            {{ formatMoney(item.discount) }}
                          </td>
                          <td class="text-gray-9 fw-medium text-end">
                            {{ formatMoney(item.total) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Totals & notes -->
                <div class="row border-bottom mb-3">
                  <div class="col-md-7">
                    <div class="py-4">
                      <div class="mb-3" v-if="invoice.description">
                        <h6 class="mb-1">Description</h6>
                        <p>{{ invoice.description }}</p>
                      </div>
                      <div class="mb-3" v-if="invoice.notes">
                        <h6 class="mb-1">Notes</h6>
                        <p>{{ invoice.notes }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-5">
                    <div
                      class="d-flex justify-content-between align-items-center border-bottom mb-2 pe-3"
                    >
                      <p class="mb-0">Sub Total</p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(totals.subTotal) }}
                      </p>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center border-bottom mb-2 pe-3"
                    >
                      <p class="mb-0">Discount</p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(totals.discountTotal) }}
                      </p>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center mb-2 pe-3"
                    >
                      <p class="mb-0">Tax</p>
                      <p class="text-dark fw-medium mb-2">
                        {{ formatMoney(totals.taxTotal) }}
                      </p>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center mb-2 pe-3"
                    >
                      <h5>Total Amount</h5>
                      <h5>{{ formatMoney(totals.grandTotal) }}</h5>
                    </div>
                    <p class="fs-12">
                      {{ amountInWords }}
                    </p>
                  </div>
                </div>

                <!-- Signature -->
                <div
                  class="row justify-content-end align-items-end text-end border-bottom mb-3"
                >
                  <div class="col-md-3">
                    <div class="text-end">
                      <img
                        v-if="signatureImage"
                        :src="signatureImage"
                        class="img-fluid"
                        alt="sign"
                      />
                      <div v-else class="invoice-sign-placeholder"></div>
                    </div>
                    <div class="text-end mb-3">
                      <h6 class="fs-14 fw-medium pe-3">
                        {{ company?.signatory_name || "Authorized Signatory" }}
                      </h6>
                      <p>{{ company?.signatory_title || "Finance Manager" }}</p>
                    </div>
                  </div>
                </div>

                <!-- Footer -->
                <div class="text-center">
                  <div class="mb-3">
                    <template v-if="companyLogo">
                      <img :src="companyLogo" class="img-fluid" alt="logo" />
                    </template>
                    <template v-else>
                      <div
                        class="avatar avatar-md bg-primary text-white d-inline-flex align-items-center justify-content-center rounded"
                      >
                        <span class="fw-bold">{{ companyInitials }}</span>
                      </div>
                    </template>
                  </div>

                  <p class="text-dark mb-1">
                    {{ paymentInstruction }}
                  </p>
                  <div class="d-flex justify-content-center align-items-center">
                    <p class="fs-12 mb-0 me-3">
                      Bank Name :
                      <span class="text-dark">
                        {{ company?.bank_name || "Bank name" }}
                      </span>
                    </p>
                    <p class="fs-12 mb-0 me-3">
                      Account Number :
                      <span class="text-dark">{{ company?.account_number || "—" }}</span>
                    </p>
                    <p class="fs-12 mb-0 me-3" v-if="company?.branch_location">
                      Branch :
                      <span class="text-dark">{{ company.branch_location }}</span>
                    </p>
                    <p class="fs-12 mb-0" v-if="company?.branch_code">
                      Branch Code :
                      <span class="text-dark">{{ company.branch_code }}</span>
                    </p>
                  </div>
                </div>
                <!-- /Footer -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.invoice-qr-fallback {
  min-height: 92px;
  border: 1px dashed #d5dbe7;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  background: #f8fafc;
}

.invoice-qr-caption {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.invoice-sign-placeholder {
  width: 140px;
  height: 48px;
  margin-left: auto;
  border-bottom: 1px solid #cfd4dc;
}
</style>
